import {AfterViewInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, Input, OnDestroy, QueryList, inject, output, viewChild, contentChild, signal, ChangeDetectionStrategy} from '@angular/core';
import {SelectOptionComponent} from 'src/app/modules/design-system/multi-select/select-option/select-option.component';
import {FormControl} from '@angular/forms';
import {merge, Observable, startWith, Subscription} from 'rxjs';
import {MatMenuTrigger, MenuPositionX, MatMenu} from '@angular/material/menu';
import {SelectSearchComponent} from 'src/app/modules/design-system/multi-select/select-search/select-search.component';
import {SelectInsertComponent} from 'src/app/modules/design-system/multi-select/select-insert/select-insert.component';
import {
	SelectOptionGroupComponent
} from 'src/app/modules/design-system/multi-select/select-option-group/select-option-group.component';
import {FocusInputService} from "../../../services/ui/focus-input.service";
import {InsertOpenedService} from "../../../services/ui/insert-opened.service";
import {TextButtonComponent} from '../text-button/text-button.component';
import {TooltipDirective} from '../tooltip/tooltip.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
	selector: 'app-multi-select',
	templateUrl: './multi-select.component.html',
	styleUrls: ['./multi-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MatMenuTrigger, MatMenu, TextButtonComponent, TooltipDirective, MatProgressSpinner, TranslatePipe]
})
export class MultiSelectComponent implements AfterViewInit, OnDestroy {
	private focusInputService = inject(FocusInputService);
	private changeDetectorRef = inject(ChangeDetectorRef);
	private insertOpenedService = inject(InsertOpenedService);

	@Input() inputControl: FormControl;
	@Input() disabled = false; // TODO @TAN inputControl.disabled instead ?
	@Input() readonly = false;
	@Input() loading = false;
	@Input() customTrigger = false;
	@Input() listenToScroll = false;
	@Input() scrollUpEvent: Observable<void>;
	@Input() confirmButton = true;
	@Input() addMinHeight = false;
	@Input() emptyPlaceholder = 'dropdown.noResult';
	@Input() emptyPlaceHolderWithSearch = 'dropdown.noResultWithSearch';
	@Input() width: number;
	@Input() xPosition: MenuPositionX = 'after';
	@Input() iconSelection = false;
	private _multiple: boolean
	@Input()
	get multiple() {
		return this._multiple;
	}

	set multiple(value: any) {
		this._multiple = value != null && `${value}` !== 'false';
	}

	@Input() enableDeleteMode = false;
	@Input() canDelete = true;
	readonly deleteSelected = output<any[]>();

	isDeleteMode = false;
	itemsForDeletion: any[] = [];

	readonly confirm = output<void>();
	readonly bottomScroll = output<void>();

	readonly menuTrigger = viewChild.required(MatMenuTrigger);
	readonly menuTrigger2 = viewChild<MatMenuTrigger>('menuTriggerRef');
	readonly triggerContainer = viewChild<ElementRef>('triggerContainer');
	readonly optionContainer = viewChild<ElementRef>('optionContainer');
	readonly deleteMenu = viewChild<MatMenuTrigger>('deleteMenu');
	readonly selectSearch = contentChild(SelectSearchComponent);
	readonly selectInsert = contentChild(SelectInsertComponent);
	@ContentChildren(SelectOptionComponent) selectOptions: QueryList<SelectOptionComponent>;
	@ContentChildren(SelectOptionGroupComponent) selectOptionGroups: QueryList<SelectOptionGroupComponent>;

	/**
	 * Derived on read, not assigned in ngAfterViewInit: the content queries are resolved
	 * before this view is checked, so the first render is already right — an assignment
	 * after the check threw NG0100 (dev mode) for a select rendered with no option.
	 */
	get isEmpty(): boolean {
		return this.selectOptions?.length === 0 && this.selectOptionGroups?.length === 0;
	}
	/** A signal: set on a timer, and the select sits in OnPush hosts. */
	readonly hasScroll = signal(false);
	optionSub: Subscription = new Subscription();
	subscription: Subscription = new Subscription();
	insertOpened = false;

	ngAfterViewInit() {
		this.subscription.add(merge(this.selectOptions.changes, this.selectOptionGroups.changes)
			.pipe(startWith({}))
			.subscribe(() => {
				this.subscribeToSelectChanges();
				this.setSelectedOptions();
				this.setChildrenProperties();
				this.setHasScroll();
				this.listenToScrollUp();
			}));
		this.subscription.add(this.inputControl.valueChanges
			.subscribe(() => this.setSelectedOptions()));
		this.subscription.add(this.insertOpenedService.getInsertOpenState()
			.subscribe((opened: boolean) => this.insertOpened = opened));
	}

	private listenToScrollUp(): void {
		if (this.listenToScroll) {
			this.subscription.add(this.scrollUpEvent.subscribe(() => {
				const optionContainer = this.optionContainer();
				if (optionContainer) {
					optionContainer.nativeElement.scrollTop = 0;
				}
			}));
		}
	}

	private subscribeToSelectChanges(): void {
		this.optionSub.unsubscribe();
		this.optionSub = new Subscription();
		this.getSelectOptionList().forEach((option: SelectOptionComponent) => {
			this.optionSub.add(option.selectChanges.subscribe(() => this.onSelectionChanges(option)));
		});
	}

	private setSelectedOptions(): void {
		this.getSelectOptionList().forEach((option: SelectOptionComponent) => {
			const selected: boolean = !this.multiple
				? this.isEquals(this.inputControl.value, option.value)
				: !!this.inputControl.value.find((v: any) => this.isEquals(v, option.value));
			option.selected.set(selected);
		});
	}

	private setChildrenProperties(): void {
		this.getSelectOptionList().forEach((option: SelectOptionComponent) => {
			option.multiple.set(this.multiple);
		});
		this.selectOptionGroups.forEach((option: SelectOptionGroupComponent) => {
			option.multiple.set(this.multiple);
		});
	}

	private onSelectionChanges(option: SelectOptionComponent): void {
		if (this.isDeleteMode) {
			if (option.selected()) {
				this.itemsForDeletion.push(option.value);
			} else {
				this.itemsForDeletion = this.itemsForDeletion.filter(v => !this.isEquals(v, option.value));
			}
			return;
		}

		if (!this.multiple) {
			this.getSelectOptionList().forEach(opt => opt.selected.set(false));
			option.selected.set(true);
			this.inputControl.setValue(option.value);
			this.menuTrigger().closeMenu();
		} else {
			if (option.selected()) {
				this.inputControl.setValue([...this.inputControl.value, option.value]);
			} else {
				this.inputControl.setValue(this.inputControl.value.filter((v: any) => !this.isEquals(v, option.value)));
			}
		}
		this.inputControl.markAsDirty();
	}

	private getSelectOptionList(): SelectOptionComponent[] {
		return this.selectOptionGroups.toArray()
			.map(group => group.selectOptions())
			.flat()
			.concat(this.selectOptions.toArray());
	}

	isEquals(v1: any, v2: any): boolean {
		if (v1 === v2) return true;
		if (!v1 && !v2) return true;
		if (!v1 || !v2) return false;
		if (typeof v1 === 'string' || typeof v2 === 'string') return false;
		const v1Ordered: Record<string, any>[] = Object.keys(v1)
			.sort((a, b) => a.localeCompare(b))
			.map(key => ({[key]: v1[key]}));
		const v2Ordered: Record<string, any>[] = Object.keys(v2)
			.sort((a, b) => a.localeCompare(b))
			.map(key => ({[key]: v2[key]}));
		return v1Ordered.length > 0 && v1Ordered.length === v2Ordered.length
			&& JSON.stringify(v1Ordered) === JSON.stringify(v2Ordered);
	}

	onOpen(): void {
		this.focusInputService.sendFocusMessage();
		this.setHasScroll();
	}

	onScroll(event: Event): void {
		if (this.listenToScroll) {
			const target = event.target as HTMLElement;
			if (target.scrollTop + target.clientHeight >= target.scrollHeight * 0.7) {
				this.bottomScroll.emit();
			}
		}
	}

	private setHasScroll(): void {
		setTimeout(() => {
			const optionContainer = this.optionContainer();
			this.hasScroll.set(!this.isEmpty && !!optionContainer && optionContainer.nativeElement.scrollHeight > optionContainer.nativeElement.clientHeight);
		});
	}

	public forceUpdate(): void {
		this.setSelectedOptions();
		this.changeDetectorRef.detectChanges();
	}

	onClose(): void {
		if (this.isDeleteMode) {
			this.isDeleteMode = false;
			this.itemsForDeletion = [];
			this.getSelectOptionList().forEach(option => {
				option.inDeleteMode.set(false);
			});
			this.setSelectedOptions();
		}

		const selectSearch = this.selectSearch();
		if (selectSearch) {
			selectSearch.searchControl.reset('');
		}
		const selectInsert = this.selectInsert();
		if (selectInsert) {
			selectInsert.createForm.reset('');
			selectInsert.openForm = false;
		}
	}

	onConfirm(): void {
		this.confirm.emit();
		this.menuTrigger().closeMenu();
	}

	toggleDeleteModeAndCloseMenu(event: MouseEvent): void {
		event.stopPropagation();

		this.toggleDeleteMode();

		const menuTrigger2 = this.menuTrigger2();
		if (menuTrigger2) {
			menuTrigger2.closeMenu();
		}
	}

	toggleDeleteMode(): void {
		this.isDeleteMode = !this.isDeleteMode;

		if (this.isDeleteMode) {
			this.selectSearch()?.searchControl.disable();

			this.itemsForDeletion = [];

			this.getSelectOptionList().forEach(option => {
				option.selected.set(false);
				option.inDeleteMode.set(true);
			});

		} else {
			this.selectSearch()?.searchControl.enable();
			this.itemsForDeletion = [];

			this.getSelectOptionList().forEach(option => {
				option.inDeleteMode.set(false);
			});
			this.setSelectedOptions();
		}
	}

	confirmDelete(): void {
		if (this.itemsForDeletion.length > 0) {
			this.deleteSelected.emit(this.itemsForDeletion);

			this.isDeleteMode = false;
			this.itemsForDeletion = [];

			this.getSelectOptionList().forEach(option => {
				option.inDeleteMode.set(false);
				option.selected.set(false);
			});

			this.setSelectedOptions();

			this.selectSearch()?.searchControl.enable();
		}
	}

	ngOnDestroy() {
		this.optionSub.unsubscribe();
		this.subscription.unsubscribe();
	}
}
